import { useMemo } from 'react';
import type { RegistryApp, RoleMapRow } from '@/api/console';
import {
    filterGroups,
    groupRoleMapRows,
    rowsInScope,
    sortGroups,
    type RoleMapFilterState,
} from './helpers';
import type { RoleMapScope, RoleMapSortDirection, RoleMapSortField } from './types';

export interface RoleMapView {
    /** RoleMap rows in the selected scope, before grouping — the "N liên kết". */
    scopedRows: RoleMapRow[];
    /** Distinct applications those links land on — the "M ứng dụng tích hợp". */
    appCount: number;
    /** Rows for the current page, already grouped/filtered/sorted. */
    pageGroups: ReturnType<typeof groupRoleMapRows>;
    /** Every group that survives the filters (drives the footer's "/ M dòng"). */
    visibleGroups: ReturnType<typeof groupRoleMapRows>;
    totalPages: number;
    /** `page` clamped into range — the caller's raw page can outrun the data. */
    page: number;
}

/**
 * EAA-1837: scope → group → filter → sort → paginate, as one pure derivation.
 *
 * Extracted from `useRoleMapTable` for the same reason `accounts/useAccountsView.ts`
 * is separate from `useAccountsScan.ts`: none of this fetches or mutates
 * anything, so keeping it apart from the query/mutation wiring means the
 * grouping maths — the part where a multi-role mapping either survives intact or
 * silently loses a role — can be read and tested on its own.
 *
 * Every step is client-side because `GET /api/v1/role-maps` accepts no query
 * params at all (`RoleMapListCreateView.get` reads none) and returns the full
 * list in one response.
 */
export function useRoleMapView(
    rows: RoleMapRow[],
    apps: RegistryApp[],
    accountCounts: Map<string, number>,
    scope: RoleMapScope,
    actingOrgId: string | null,
    filters: RoleMapFilterState,
    sortField: RoleMapSortField,
    sortDirection: RoleMapSortDirection,
    page: number,
    pageSize: number
): RoleMapView {
    const scopedRows = useMemo(() => rowsInScope(rows, scope, actingOrgId), [rows, scope, actingOrgId]);

    const appCount = useMemo(
        () => new Set(scopedRows.map((row) => row.application)).size,
        [scopedRows]
    );

    const allGroups = useMemo(
        () => groupRoleMapRows(scopedRows, apps, accountCounts),
        [scopedRows, apps, accountCounts]
    );

    const filtered = useMemo(() => filterGroups(allGroups, filters), [allGroups, filters]);

    const visibleGroups = useMemo(
        () => sortGroups(filtered, sortField, sortDirection),
        [filtered, sortField, sortDirection]
    );

    const totalPages = Math.max(1, Math.ceil(visibleGroups.length / pageSize));
    const clampedPage = Math.min(page, totalPages);
    const pageGroups = useMemo(
        () => visibleGroups.slice((clampedPage - 1) * pageSize, clampedPage * pageSize),
        [visibleGroups, clampedPage, pageSize]
    );

    return { scopedRows, appCount, pageGroups, visibleGroups, totalPages, page: clampedPage };
}
